import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolMatches, docInScope, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';
import StatCards from './StatCards';
import './AdminTranscriptsView.css';
import './AdminDetailPages.css';

const DEFAULT_SCALE = [
  { letter: 'A+', min: 97, gpa: 4.3 }, { letter: 'A', min: 93, gpa: 4.0 }, { letter: 'A-', min: 90, gpa: 3.7 },
  { letter: 'B+', min: 87, gpa: 3.3 }, { letter: 'B', min: 83, gpa: 3.0 }, { letter: 'B-', min: 80, gpa: 2.7 },
  { letter: 'C+', min: 77, gpa: 2.3 }, { letter: 'C', min: 73, gpa: 2.0 }, { letter: 'C-', min: 70, gpa: 1.7 },
  { letter: 'D+', min: 67, gpa: 1.3 }, { letter: 'D', min: 63, gpa: 1.0 }, { letter: 'D-', min: 60, gpa: 0.7 },
  { letter: 'F', min: 0, gpa: 0.0 },
];

const tsMs = (ts) => {
  if (!ts) return 0;
  if (ts.seconds) return ts.seconds * 1000;
  if (ts.toMillis) return ts.toMillis();
  return new Date(ts).getTime() || 0;
};

function AdminTranscriptsView({ adminData }) {
  const [transcripts, setTranscripts] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [toast, setToast] = useState('');

  const schoolIds = useMemo(() => adminSchoolIds(adminData), [adminData]);

  const loadAll = useCallback(async () => {
    if (!adminData) return;
    setLoading(true);
    try {
      const ck = (p) => `${schoolIds.join('|') || 'all'}:${p}`;
      const [tDocs, classDocs] = await Promise.all([
        dataCacheService.getDocs(ck('transcripts'), collection(db, 'transcripts')),
        dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
      ]);
      setTranscripts(tDocs.filter(t => docInScope(t.schoolId, schoolIds)));

      const classesData = classDocs.filter(c => docInScope(c.schoolId, schoolIds));
      const perClass = await Promise.all(classesData.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const allStudents = perClass.flat().filter(s => s.isActive !== false);
      setStudents(allStudents);
    } catch (e) {
      console.error('🔥 AdminTranscripts: load error', e);
    } finally {
      setLoading(false);
    }
  }, [adminData, schoolIds]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filtered = transcripts.filter(t =>
    !searchText ||
    (t.studentName || '').toLowerCase().includes(searchText.toLowerCase()) ||
    (t.studentExternalId || '').toLowerCase().includes(searchText.toLowerCase())
  );

  const avgGpa = transcripts.length
    ? (transcripts.reduce((a, t) => a + (t.cumulativeGPA || 0), 0) / transcripts.length).toFixed(2)
    : '0.00';

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading transcripts…</p></div>;
  }

  if (selected) {
    return <TranscriptDetail transcript={selected} onBack={() => setSelected(null)} onDeleted={() => { setSelected(null); loadAll(); }} />;
  }

  return (
    <div className="admin-transcripts-view">
      <div className="view-header">
        <h2>Transcripts</h2>
        <button className="add-button" onClick={() => setShowGenerate(true)}>+ Generate Transcript</button>
      </div>

      <StatCards items={[
        ['Transcripts', transcripts.length],
        ['Students', new Set(transcripts.map(t => t.studentId)).size],
        ['Avg GPA', avgGpa],
      ]} />

      {toast && <div className="success-banner">{toast}</div>}

      <input
        className="transcript-search"
        type="text"
        placeholder="Search by student name or ID…"
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
      />

      {filtered.length === 0 ? (
        <div className="empty-state">
          <p>No transcripts yet — click <strong>Generate Transcript</strong> and pick a student. The transcript is built automatically from their grades, attendance, and enrollment history.</p>
        </div>
      ) : (
        <div className="transcript-grid">
          {filtered.map(t => (
            <button key={t.id} className="transcript-card" onClick={() => setSelected(t)}>
              <div className="transcript-card-top">
                <span className="transcript-icon">📄</span>
                <div>
                  <strong>{t.studentName}</strong>
                  <span className="record-id">{t.studentExternalId}</span>
                </div>
              </div>
              <div className="transcript-card-stats">
                <span>Years: {t.yearSummaries?.length || 0}</span>
                <span>Subjects: {(t.yearSummaries || []).reduce((a, y) => a + (y.subjectRecords?.length || 0), 0)}</span>
                <span>GPA: {(t.cumulativeGPA ?? 0).toFixed(2)}</span>
                <span>Att: {((t.cumulativeAttendanceRate ?? 0) * 100).toFixed(0)}%</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showGenerate && (
        <GenerateTranscriptModal
          adminData={adminData}
          students={students}
          transcripts={transcripts}
          onClose={() => setShowGenerate(false)}
          onDone={(name) => { setShowGenerate(false); setToast(`Transcript generated for ${name}`); dataCacheService.invalidatePath('transcripts'); loadAll(); }}
        />
      )}
    </div>
  );
}

// ---------- Generate ----------

function GenerateTranscriptModal({ adminData, students, transcripts, onClose, onDone }) {
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const schoolIds = adminSchoolIds(adminData);
  const filtered = students.filter(s =>
    !search ||
    (s.fullName || `${s.firstName || ''} ${s.lastName || ''}`).toLowerCase().includes(search.toLowerCase()) ||
    (s.studentId || '').toLowerCase().includes(search.toLowerCase())
  );

  const generate = async (student) => {
    setBusyId(student.id);
    setError('');
    try {
      const ck = (p) => `${schoolIds.join('|') || 'all'}:${p}`;

      // Everything except users/schools lives nested under classes/{id}/…
      const classDocs = (await dataCacheService.getDocs(ck('classes'), collection(db, 'classes')))
        .filter(c => docInScope(c.schoolId, schoolIds));
      const classById = new Map(classDocs.map(c => [String(c.id), c]));

      const [assignmentArrays, attendArrays, classSubjectArrays,
             globalSubjects, enrollmentDocs, scaleDocs, yearDocs, levelDocs, userDocs, schoolDoc] = await Promise.all([
        Promise.all(classDocs.map(c =>
          dataCacheService.getDocs(ck(`classes/${c.id}/assignments`), collection(db, 'classes', c.id, 'assignments')))),
        Promise.all(classDocs.map(c =>
          dataCacheService.getDocs(ck(`classes/${c.id}/attendanceRecords`), collection(db, 'classes', c.id, 'attendanceRecords')))),
        Promise.all(classDocs.map(c =>
          dataCacheService.getDocs(ck(`classes/${c.id}/subjects`), collection(db, 'classes', c.id, 'subjects')))),
        dataCacheService.getDocs(ck('subjects'), collection(db, 'subjects')),
        dataCacheService.getDocs(ck('enrollmentRecords'), collection(db, 'enrollmentRecords')),
        dataCacheService.getDocs(ck('gradingScales'), collection(db, 'gradingScales')),
        dataCacheService.getDocs(ck('academicYears'), collection(db, 'academicYears')),
        dataCacheService.getDocs(ck('gradeLevels'), collection(db, 'gradeLevels')),
        dataCacheService.getDocs(ck('users'), collection(db, 'users')),
        adminData?.schoolId
          ? dataCacheService.getDoc(ck(`schools/${adminData.schoolId}`), doc(db, 'schools', adminData.schoolId))
          : Promise.resolve(null),
      ]);

      // Attach classId to each assignment (subcollection docs may not carry it)
      const assignments = assignmentArrays.flatMap((arr, i) =>
        arr.map(a => ({ ...a, classId: a.classId || classDocs[i].id })));
      const subjects = [...globalSubjects, ...classSubjectArrays.flat()];
      const attendDocs = attendArrays.flat();
      const levelById = new Map(levelDocs.map(l => [String(l.id), l]));
      const userNameById = new Map();
      userDocs.forEach(u => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
        if (name) { userNameById.set(String(u.id), name); }
      });

      const enrollments = enrollmentDocs
        .filter(e => String(e.studentId) === String(student.id));
      const years = yearDocs
        .filter(y => !y.schoolId || schoolIds.some(id => schoolMatches(y.schoolId, id)));

      const scaleDoc = scaleDocs
        .find(s => !s.schoolId || schoolIds.some(id => schoolMatches(s.schoolId, id)));
      const scale = (scaleDoc?.letterGrades || DEFAULT_SCALE)
        .map(g => ({ letter: g.letter, min: g.minPercentage ?? g.min ?? 0, gpa: g.gpaPoints ?? g.gpa ?? 0 }))
        .sort((a, b) => b.min - a.min);
      const letterFor = (pct) => (scale.find(g => pct >= g.min) || scale[scale.length - 1]);

      // Grades for this student live under each class's assignments:
      // classes/{cid}/assignments/{aid}/grades/{gid} (parallel, cached)
      const studentGrades = [];
      const gradesPerAssignment = await Promise.all(assignments.map(a =>
        dataCacheService.getDocs(ck(`classes/${a.classId}/assignments/${a.id}/grades`), collection(db, 'classes', a.classId, 'assignments', a.id, 'grades'))
      ));
      assignments.forEach((a, i) => gradesPerAssignment[i].forEach(g => {
        if (String(g.studentId) === String(student.id)) {
          studentGrades.push({ ...g, assignmentId: a.id, classId: a.classId });
        }
      }));
      // Also check top-level grades collection (newer writes)
      const flatDocs = await dataCacheService.getDocs(ck('grades'), collection(db, 'grades'));
      flatDocs.forEach(g => {
        if (String(g.studentId) === String(student.id) &&
            !studentGrades.some(x => String(x.id) === String(g.id))) {
          studentGrades.push(g);
        }
      });

      const attendance = attendDocs
        .filter(a => String(a.studentId) === String(student.id));

      const studentClass = classById.get(String(student.classId));
      const fallbackGradeId = enrollments[enrollments.length - 1]?.gradeId || student.gradeId || '';
      const fallbackGradeName = enrollments[enrollments.length - 1]?.gradeName
        || levelById.get(String(student.gradeId))?.name
        || studentClass?.grade || '';
      const fallbackClassId = enrollments[enrollments.length - 1]?.classId || student.classId || '';
      const fallbackClassName = enrollments[enrollments.length - 1]?.className
        || studentClass?.name || '';

      const inYear = (dateVal, year) => {
        const ms = tsMs(dateVal);
        return ms >= tsMs(year.startDate) && ms <= tsMs(year.endDate);
      };

      const subjectRecordsFor = (grades, yearId, yearName, enrollment) => {
        const bySubject = {};
        for (const g of grades) {
          const subjectId = assignments.find(a => String(a.id) === String(g.assignmentId))?.subjectId;
          if (!subjectId) continue;
          (bySubject[subjectId] = bySubject[subjectId] || []).push(g);
        }
        return Object.entries(bySubject).map(([subjectId, subjectGrades]) => {
          const subject = subjects.find(s => String(s.id) === String(subjectId));
          const avg = subjectGrades.reduce((a, g) => a + (g.maxScore > 0 ? (g.score / g.maxScore) * 100 : 0), 0) / subjectGrades.length;
          const lg = letterFor(avg);
          return {
            id: crypto.randomUUID(),
            subjectId, subjectName: subject?.name || 'Subject', subjectCategory: subject?.category || '',
            gradeId: enrollment?.gradeId || fallbackGradeId,
            gradeName: enrollment?.gradeName || fallbackGradeName,
            academicYearId: yearId, academicYearName: yearName,
            averageScore: avg, letterGrade: lg.letter, gpaPoints: lg.gpa,
            teacherName: userNameById.get(String(subject?.teacherId || '')) || null,
            assignmentCount: subjectGrades.length,
          };
        }).sort((a, b) => a.subjectName.localeCompare(b.subjectName));
      };

      const attendanceRateBetween = (start, end) => {
        const recs = attendance.filter(a => {
          const ms = tsMs(a.date);
          return (!start || (ms >= tsMs(start) && ms <= tsMs(end)));
        });
        const attended = recs.filter(a => ['present', 'late'].includes(String(a.status || '').toLowerCase())).length;
        return recs.length ? attended / recs.length : 0;
      };

      // Years covered: enrollment records or grades inside the year dates.
      // Fall back to all non-archived years, then a single cumulative bucket.
      let coveredYears = years.filter(y =>
        enrollments.some(e => String(e.academicYearId) === String(y.id)) ||
        studentGrades.some(g => inYear(g.gradedAt, y))
      );
      if (coveredYears.length === 0) {
        coveredYears = years.filter(y => !y.isArchived);
      }

      let yearSummaries = coveredYears
        .sort((a, b) => tsMs(a.startDate) - tsMs(b.startDate))
        .map(year => {
          const yearGrades = studentGrades.filter(g => inYear(g.gradedAt, year));
          const enrollment = enrollments.find(e => String(e.academicYearId) === String(year.id));
          const subjectRecords = subjectRecordsFor(yearGrades, year.id, year.name, enrollment);
          const overallAverage = subjectRecords.length
            ? subjectRecords.reduce((a, s) => a + s.averageScore, 0) / subjectRecords.length : 0;

          return {
            id: crypto.randomUUID(),
            academicYearId: year.id, academicYearName: year.name,
            gradeId: enrollment?.gradeId || fallbackGradeId,
            gradeName: enrollment?.gradeName || fallbackGradeName,
            classId: enrollment?.classId || fallbackClassId,
            className: enrollment?.className || fallbackClassName,
            overallAverage,
            overallGPA: subjectRecords.length
              ? subjectRecords.reduce((a, s) => a + s.gpaPoints, 0) / subjectRecords.length : 0,
            attendanceRate: attendanceRateBetween(year.startDate, year.endDate),
            totalAssignments: yearGrades.length,
            completedAssignments: yearGrades.length,
            subjectRecords,
          };
        });

      if (yearSummaries.length === 0) {
        const subjectRecords = subjectRecordsFor(studentGrades, crypto.randomUUID(), 'Cumulative Record', null);
        const attended = attendance.filter(a => ['present', 'late'].includes(String(a.status || '').toLowerCase())).length;
        yearSummaries = [{
          id: crypto.randomUUID(),
          academicYearId: crypto.randomUUID(), academicYearName: 'Cumulative Record',
          gradeId: fallbackGradeId, gradeName: fallbackGradeName,
          classId: fallbackClassId, className: fallbackClassName,
          overallAverage: subjectRecords.length
            ? subjectRecords.reduce((a, s) => a + s.averageScore, 0) / subjectRecords.length : 0,
          overallGPA: subjectRecords.length
            ? subjectRecords.reduce((a, s) => a + s.gpaPoints, 0) / subjectRecords.length : 0,
          attendanceRate: attendance.length ? attended / attendance.length : 0,
          totalAssignments: studentGrades.length,
          completedAssignments: studentGrades.length,
          subjectRecords,
        }];
      }

      const cumulativeGPA = yearSummaries.length
        ? yearSummaries.reduce((a, y) => a + y.overallGPA, 0) / yearSummaries.length : 0;
      const cumulativeAttendance = yearSummaries.length
        ? yearSummaries.reduce((a, y) => a + y.attendanceRate, 0) / yearSummaries.length : 0;

      const enrollmentDate = enrollments.map(e => tsMs(e.enrolledAt)).filter(Boolean).sort()[0]
        || tsMs(student.createdAt) || null;

      // Reuse the student's existing transcript doc so regenerating updates it
      const existing = transcripts.find(t => String(t.studentId) === String(student.id));
      const id = existing?.id || crypto.randomUUID();
      const name = student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim();
      await setDoc(doc(db, 'transcripts', id), {
        id,
        studentId: student.id,
        studentName: name,
        studentExternalId: student.studentId || '',
        birthDate: student.birthDate || null,
        schoolId: adminData?.schoolId || null,
        schoolName: schoolDoc?.name || null,
        enrollmentDate: enrollmentDate ? new Date(enrollmentDate).toISOString() : null,
        yearSummaries,
        cumulativeGPA,
        cumulativeAttendanceRate: cumulativeAttendance,
        generatedAt: new Date().toISOString(),
        generatedBy: adminData?.uid || adminData?.id || '',
      });
      onDone(name);
    } catch (e) {
      console.error('🔥 Transcript generation error:', e);
      setError(e.message || 'Failed to generate transcript');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Generate Transcript</h3>
        <p className="modal-subtitle">Pick a student — the transcript is built from their grades, attendance, and enrollment history.</p>
        <input type="text" placeholder="Search students…" value={search} onChange={e => setSearch(e.target.value)} />
        {error && <div className="error-message">{error}</div>}
        <div className="generate-list">
          {filtered.map(s => (
            <button key={s.id} className="generate-row" disabled={!!busyId} onClick={() => generate(s)}>
              <span>
                <strong>{s.fullName || `${s.firstName || ''} ${s.lastName || ''}`}</strong>
                <span className="record-id"> {s.studentId}</span>
              </span>
              {busyId === s.id ? '…' : '📄'}
            </button>
          ))}
          {filtered.length === 0 && <p className="detail-empty-text">No students found.</p>}
        </div>
        <button className="detail-btn-neutral" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

// ---------- Detail ----------

function TranscriptDetail({ transcript, onBack, onDeleted }) {
  const [notice, setNotice] = useState('');

  const fmtDate = (v) => {
    const ms = tsMs(v);
    return ms ? new Date(ms).toLocaleDateString() : null;
  };

  const gradeBadgeClass = (letter) => {
    const c = (letter || '').charAt(0).toUpperCase();
    return { A: 'grade-a', B: 'grade-b', C: 'grade-c', D: 'grade-d', F: 'grade-f' }[c] || 'grade-na';
  };

  const transcriptText = () => {
    const lines = [
      transcript.schoolName || 'Official Transcript',
      transcript.schoolName ? 'Official Transcript' : null,
      `Generated: ${fmtDate(transcript.generatedAt) || '—'}`,
      '',
      (transcript.studentName || '').trim(),
      `Student ID: ${transcript.studentExternalId || '—'}`,
      transcript.birthDate ? `Date of Birth: ${fmtDate(transcript.birthDate)}` : null,
      transcript.enrollmentDate ? `Enrolled Since: ${fmtDate(transcript.enrollmentDate)}` : null,
      transcript.graduationDate ? `Graduated: ${fmtDate(transcript.graduationDate)}` : null,
      `Cumulative GPA: ${(transcript.cumulativeGPA ?? 0).toFixed(2)}  Attendance: ${((transcript.cumulativeAttendanceRate ?? 0) * 100).toFixed(0)}%`,
      '',
    ];
    (transcript.yearSummaries || []).forEach(y => {
      lines.push(`${y.academicYearName} — ${[y.gradeName, y.className].filter(Boolean).join(' ')}`);
      lines.push(`  Average ${(y.overallAverage ?? 0).toFixed(1)}% • GPA ${(y.overallGPA ?? 0).toFixed(2)} • Attendance ${((y.attendanceRate ?? 0) * 100).toFixed(0)}% • ${y.completedAssignments}/${y.totalAssignments} assignments`);
      (y.subjectRecords || []).forEach(s => {
        lines.push(`  ${s.subjectName}: ${s.letterGrade} (${(s.averageScore ?? 0).toFixed(1)}%, ${(s.gpaPoints ?? 0).toFixed(1)} pts)${s.teacherName ? ` — ${s.teacherName}` : ''}`);
      });
    });
    return lines.filter(l => l !== null && l !== undefined).join('\n');
  };

  const share = async () => {
    const text = transcriptText();
    try {
      if (navigator.share) {
        await navigator.share({ title: `Transcript — ${(transcript.studentName || '').trim()}`, text });
      } else {
        await navigator.clipboard.writeText(text);
        setNotice('Transcript copied to clipboard');
        setTimeout(() => setNotice(''), 3000);
      }
    } catch (e) { /* user cancelled share */ }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this transcript for ${(transcript.studentName || '').trim()}? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'transcripts', transcript.id));
      dataCacheService.invalidatePath('transcripts');
      onDeleted();
    } catch (e) {
      setNotice('Failed to delete transcript');
    }
  };

  const totalSubjects = (transcript.yearSummaries || []).reduce((a, y) => a + (y.subjectRecords?.length || 0), 0);

  return (
    <div className="admin-transcripts-view">
      <div className="detail-page">
        <div className="transcript-actions">
          <button className="detail-back-button" onClick={onBack}>◄ Back to Transcripts</button>
          <div className="transcript-action-buttons">
            <button className="detail-btn-primary" onClick={share}>↗ Share</button>
            <button className="detail-btn-neutral" onClick={() => window.print()}>🖨️ Save / Print</button>
            <button className="detail-btn-danger" onClick={remove}>🗑️ Delete</button>
          </div>
        </div>
        {notice && <div className="success-banner">{notice}</div>}

        <div className="detail-card transcript-print-area">
          <div className="transcript-school-header">
            <h2>{transcript.schoolName || 'Official Transcript'}</h2>
            {transcript.schoolName && <p className="transcript-subtitle">Official Transcript</p>}
            <p className="transcript-generated">Generated {fmtDate(transcript.generatedAt) || '—'}</p>
          </div>

          <div className="transcript-student-card">
            <h3>{(transcript.studentName || '').trim()}</h3>
            <p className="detail-subtitle">Student ID: {transcript.studentExternalId || '—'}</p>
            <div className="transcript-info-rows">
              {transcript.birthDate && <div className="transcript-info-row"><span>Date of Birth</span><strong>{fmtDate(transcript.birthDate)}</strong></div>}
              {transcript.enrollmentDate && <div className="transcript-info-row"><span>Enrolled Since</span><strong>{fmtDate(transcript.enrollmentDate)}</strong></div>}
              {transcript.graduationDate && <div className="transcript-info-row"><span>Graduated</span><strong>{fmtDate(transcript.graduationDate)}</strong></div>}
            </div>
            <div className="transcript-stat-pills">
              <div className="transcript-stat-pill gpa"><span className="pill-value">{(transcript.cumulativeGPA ?? 0).toFixed(2)}</span><span className="pill-label">Cumulative GPA</span></div>
              <div className="transcript-stat-pill attendance"><span className="pill-value">{((transcript.cumulativeAttendanceRate ?? 0) * 100).toFixed(0)}%</span><span className="pill-label">Attendance</span></div>
              <div className="transcript-stat-pill years"><span className="pill-value">{transcript.yearSummaries?.length || 0}</span><span className="pill-label">Years</span></div>
              <div className="transcript-stat-pill subjects"><span className="pill-value">{totalSubjects}</span><span className="pill-label">Subjects</span></div>
            </div>
          </div>

          {(transcript.yearSummaries || []).map(y => (
            <div key={y.id} className="transcript-year-card">
              <div className="transcript-year-header">
                <h3>{y.academicYearName}</h3>
                {y.gradeName && <span className="transcript-grade-chip">{y.gradeName}</span>}
              </div>
              {y.className && <p className="transcript-class-line">Class: {y.className}</p>}
              <p className="transcript-year-stats">
                Average {(y.overallAverage ?? 0).toFixed(1)}% · GPA {(y.overallGPA ?? 0).toFixed(2)} · Attendance {((y.attendanceRate ?? 0) * 100).toFixed(0)}% · {y.completedAssignments}/{y.totalAssignments} assignments
              </p>
              {(y.subjectRecords || []).length > 0 && (
                <div className="table-wrap">
                <table className="detail-table transcript-table">
                  <thead><tr><th>Subject</th><th>Teacher</th><th>Average</th><th>Grade</th><th>GPA</th><th>Assignments</th></tr></thead>
                  <tbody>
                    {y.subjectRecords.map(s => (
                      <tr key={s.id}>
                        <td><strong>{s.subjectName}</strong>{s.subjectCategory ? <span className="record-id">{s.subjectCategory}</span> : null}</td>
                        <td>{s.teacherName || '—'}</td>
                        <td>{(s.averageScore ?? 0).toFixed(1)}%</td>
                        <td><span className={`grade-badge ${gradeBadgeClass(s.letterGrade)}`}>{s.letterGrade || '—'}</span></td>
                        <td>{(s.gpaPoints ?? 0).toFixed(1)}</td>
                        <td>{s.assignmentCount ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          ))}
          {(transcript.yearSummaries || []).length === 0 && (
            <p className="detail-empty-text">This transcript has no year summaries.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminTranscriptsView;
